import React, { useState, useCallback } from 'react';
import { DEFAULT_CUSTOM_SCHEDULE } from '../constants';
import {
  initializeFormData,
  isConnectionField,
  validateForm,
  getScenarioFieldDefaults,
  convertCustomScheduleToObject,
  convertLocalHourToUTC,
  getDayRollForLocalToUTC
} from '../utils';
import { transformFormToPayload } from '../utils/transformFormToPayload';
import * as agentScannerService from '~/services/agentScannerService';
import {
  getConnectionFieldNamesForAuthMethod,
  getSensitiveFieldNamesForAuthMethod,
  getScenarioConfigFromProviders
} from '../AuthenticationFields/agentProvidersService';

/**
 * Checks if a field is sensitive (requires special handling in edit mode).
 * Derived from Agent Providers JSON (fields with type "password").
 */
const isSensitiveField = (field, authMethod, context) => {
  const sensitiveFields = getSensitiveFieldNamesForAuthMethod(
    authMethod,
    context
  );

  return sensitiveFields.includes(field);
};

/**
 * Removes old auth-specific fields from state. Field list from Agent Providers JSON.
 */
const removeAuthFields = (state, authMethod, context) => {
  const fieldsToRemove = getConnectionFieldNamesForAuthMethod(
    authMethod,
    context
  );
  const cleanedState = { ...state };

  fieldsToRemove.forEach((field) => {
    delete cleanedState[field];
  });

  return cleanedState;
};

/**
 * Handles target system change and returns new state.
 * Compatible auth methods derived from Agent Providers JSON.
 */
const handleTargetSystemChange = (prevState, newTargetSystem, context) => {
  const cleanedState = removeAuthFields(
    prevState,
    prevState.authMethod,
    context
  );

  return {
    ...cleanedState,
    targetSystem: newTargetSystem,
    authMethod: ''
  };
};

/**
 * Handles auth method change and returns new state
 */
const handleAuthMethodChange = (prevState, newAuthMethod, context) => {
  const cleanedState = removeAuthFields(
    prevState,
    prevState.authMethod,
    context
  );

  // Get scenario-specific field defaults
  const newAuthFields = getScenarioFieldDefaults(
    prevState.targetSystem,
    newAuthMethod,
    context
  );

  return {
    ...cleanedState,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Blurs the run-frequency select element after modal closes
 */
const blurRunFrequencySelect = () => {
  setTimeout(() => {
    const selectElement = document.getElementById('run-frequency');

    if (selectElement) {
      selectElement.blur();
    }
  }, 0);
};

/**
 * Processes custom schedule field change
 */
const processCustomScheduleChange = (prevSchedule, field, value) => {
  const newSchedule = {
    ...prevSchedule,
    [field]: value
  };

  // Reset fields when switching to months
  if (field === 'intervalUnit' && value === 'months') {
    newSchedule.intervalNumber = 1;
    newSchedule.repeatDays = [];
  }

  // Clear repeat days when switching away from weeks
  const isSwitchingFromWeeks =
    field === 'intervalUnit' &&
    value !== 'weeks' &&
    prevSchedule.intervalUnit === 'weeks';

  if (isSwitchingFromWeeks) {
    newSchedule.repeatDays = [];
  }

  return newSchedule;
};

/**
 * Helper to get sensitive field value for API submission
 * In edit mode: returns null if field wasn't modified (preserves existing secret)
 * In edit mode: returns actual value if field was modified (updates secret)
 * In add mode: always returns actual value
 */
const getSensitiveFieldValue = (
  fieldName,
  fieldValue,
  authMethod,
  mode,
  modifiedSensitiveFields,
  context
) => {
  if (mode === 'edit' && isSensitiveField(fieldName, authMethod, context)) {
    if (!modifiedSensitiveFields.has(fieldName)) {
      return null; // Preserve existing value
    }
  }

  return fieldValue ?? null;
};

/**
 * Build auth parameters object with sensitive field handling.
 * Field set derived from Agent Providers JSON; request structure unchanged.
 */
const buildAuthParameters = (
  formData,
  mode,
  modifiedSensitiveFields,
  context
) => {
  const getSensitiveValue = (field, value) =>
    getSensitiveFieldValue(
      field,
      value,
      formData.authMethod,
      mode,
      modifiedSensitiveFields,
      context
    );

  const params = {
    target: formData.targetSystem,
    method: formData.authMethod
  };

  const scenarioConfig = getScenarioConfigFromProviders(
    formData.targetSystem,
    formData.authMethod,
    context
  );

  if (scenarioConfig?.fields) {
    for (const field of scenarioConfig.fields) {
      const value = formData[field.id];

      params[field.id] =
        field.type === 'password'
          ? getSensitiveValue(field.id, value)
          : (value ?? '');
    }
  }

  return params;
};

/**
 * Build run policy schedule object from form data.
 * All times sent to the backend are in UTC.
 */
const buildRunPolicySchedule = (formData) => {
  const utcHour = convertLocalHourToUTC(formData.at);

  const schedule = {
    at: utcHour,
    syncReview: formData.syncReview
  };

  // Handle structured runFrequency object
  if (formData.runFrequency && typeof formData.runFrequency === 'object') {
    const { unit, interval, on } = formData.runFrequency;

    schedule.unit = unit || 'day';
    schedule.interval = String(interval || 1);

    if (on !== undefined) {
      // When converting local time to UTC, the hour can cross midnight (e.g. 16th 11pm → 17th 02:00 UTC).
      // We must send the UTC day (of week or of month) so the backend schedules the correct run.
      const dayRoll = getDayRollForLocalToUTC(formData.at);
      const onNum = typeof on === 'string' ? parseInt(on, 10) : on;

      if (unit === 'week') {
        schedule.on = (onNum + dayRoll + 7) % 7;
      } else if (unit === 'month') {
        // Day of month 1–31; roll can push to 0 or 32 (backend may treat 32 as 1st of next month).
        const adjusted = onNum + dayRoll;

        if (adjusted < 1) {
          schedule.on = 1;
        } else if (adjusted > 31) {
          schedule.on = 32;
        } else {
          schedule.on = adjusted;
        }
      } else {
        schedule.on = on;
      }
    }
  } else {
    // Fallback for string values (backward compatibility)
    schedule.unit = 'day';
    schedule.interval = '1';
  }

  // Add optional advanced settings
  if (formData.timeout) {
    schedule.timeout = formData.timeout;
  }
  if (formData.maxRetryAttempts) {
    schedule.maxRetryAttempts = formData.maxRetryAttempts;
  }

  return schedule;
};

/**
 * Build complete form payload for API submission
 */
const buildFormPayload = (
  formData,
  mode,
  modifiedSensitiveFields,
  context
) => ({
  name: formData.name,
  description: formData.description,
  parameters: buildAuthParameters(
    formData,
    mode,
    modifiedSensitiveFields,
    context
  ),
  runPolicy: buildRunPolicySchedule(formData),
  emailNotificationsEnabled: formData.emailNotificationsEnabled,
  recipientEmails: formData.recipientEmails
});

/**
 * Custom hook for managing scanner configuration form state and handlers
 *
 * @param {Object} initialData - Initial form data from props
 * @param {string} mode - Form mode ('add' or 'edit')
 * @param {Function} onSubmit - Submit callback
 * @param {Object} context - Additional context for API payload
 * @param {string} context.organizationId - Organization ID
 * @param {string} context.ownerId - Owner ID
 * @param {string} context.businessGroup - Business Group ID
 * @param {string} context.targetSystemId - Target System UUID
 * @returns {Object} Form state and handlers
 */
const useScannerForm = (
  initialData,
  mode,
  onSubmit,
  context = {},
  resetNameError = () => {}
) => {
  // Form data state - initialized synchronously
  const [formData, setFormData] = useState(() =>
    initializeFormData(initialData, mode, context)
  );

  // Track which sensitive fields have been modified in edit mode
  const [modifiedSensitiveFields, setModifiedSensitiveFields] = useState(
    new Set()
  );

  // UI state
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [connectionError, setConnectionError] = useState(null);
  const [connectionHeaderError, setConnectionHeaderError] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(DEFAULT_CUSTOM_SCHEDULE);

  // Scheduled scan status state
  // Initializes from state field for edit mode, or SCHEDULED for add mode
  const [scheduleStatus, setScheduleStatus] = useState(() => {
    if (mode === 'edit' && initialData?.state) {
      return initialData.state;
    }

    return 'SCHEDULED';
  });

  // Reset connection status when connection fields change
  const resetConnectionStatusIfNeeded = useCallback(
    (field) => {
      const isConn = isConnectionField(field, formData.authMethod, context);

      if (isConn && connectionStatus !== 'idle') {
        setConnectionStatus('idle');
        setConnectionError(null);
        setConnectionHeaderError(null);
      }
    },
    [connectionStatus, context, formData.authMethod]
  );

  // Generic change handler for all form fields
  const handleChange = useCallback(
    (field, value) => {
      if (field === 'name') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (resetNameError) {
          resetNameError();
        }

        return;
      }

      // Handle special field types with early returns
      if (field === 'targetSystem') {
        setFormData((prev) => handleTargetSystemChange(prev, value, context));
        resetConnectionStatusIfNeeded(field);
        setModifiedSensitiveFields(new Set());

        return;
      }

      if (field === 'authMethod') {
        setFormData((prev) => handleAuthMethodChange(prev, value, context));
        resetConnectionStatusIfNeeded(field);
        setModifiedSensitiveFields(new Set());

        return;
      }

      if (field === 'runFrequency' && value === 'Custom') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setShowCustomModal(true);
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Track modifications to sensitive fields in edit mode
      if (mode === 'edit') {
        if (isSensitiveField(field, formData.authMethod, context)) {
          setModifiedSensitiveFields((prev) => new Set(prev).add(field));
        }
      }

      // Handle all other fields
      setFormData((prev) => ({ ...prev, [field]: value }));
      resetConnectionStatusIfNeeded(field);
    },
    [resetConnectionStatusIfNeeded, mode, formData.authMethod, context]
  );

  // Handle custom schedule modal save
  const handleCustomScheduleSave = useCallback(() => {
    const structuredSchedule = convertCustomScheduleToObject(customSchedule);

    setFormData((prev) => ({ ...prev, runFrequency: structuredSchedule }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, [customSchedule]);

  // Handle custom schedule modal cancel
  const handleCustomScheduleCancel = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      runFrequency: { unit: 'day', interval: 1 }
    }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, []);

  // Handle custom schedule field changes
  const handleCustomScheduleChange = useCallback((field, value) => {
    setCustomSchedule((prev) =>
      processCustomScheduleChange(prev, field, value)
    );
  }, []);

  // Toggle advanced settings accordion
  const toggleAdvancedSettings = useCallback(() => {
    setShowAdvancedSettings((prev) => !prev);
  }, []);

  // Toggle email notifications
  const handleEmailNotificationsToggle = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      emailNotificationsEnabled: !prev.emailNotificationsEnabled
    }));
  }, []);

  // Handle scheduled scan status toggle
  const handleScheduleStatusToggle = useCallback(() => {
    setScheduleStatus((prevStatus) => {
      if (prevStatus === 'SCHEDULED') {
        return 'PAUSED';
      }

      return 'SCHEDULED';
    });
  }, []);

  // Add email to recipient list
  const handleAddEmail = useCallback((email) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, email]
    }));
  }, []);

  // Remove email from recipient list
  const handleRemoveEmail = useCallback((emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter(
        (email) => email !== emailToRemove
      )
    }));
  }, []);

  // Handle connection test
  // Returns true if connection test succeeds, false otherwise
  const handleTestConnection = useCallback(async () => {
    setConnectionStatus('checking');
    setConnectionError(null);
    setConnectionHeaderError(null);

    try {
      // Reuse buildAuthParameters for consistency (use 'add' mode to get actual values, not nulls)
      const formPayloadData = {
        name: formData.name || 'Test Connection',
        description: '',
        parameters: buildAuthParameters(formData, 'add', new Set(), context),
        runPolicy: {
          runFrequency: 'Daily',
          runTime: '2',
          syncReview: 'auto-resolve'
        }
      };

      // Transform to API payload to get connection structure
      const apiPayload = transformFormToPayload(formPayloadData, context);

      // Extract connection parameters for test
      const { connection } = apiPayload;

      // Parse authParameters from string to JSON object
      const authParams = JSON.parse(connection.authParameters);

      // Get connection ID (null if not defined)
      const connectionId =
        formData.connectionId || initialData?.connection?.id || null;

      // Set sensitive fields to null if not provided by user (from scenario config)
      const scenarioConfig = getScenarioConfigFromProviders(
        formData.targetSystem,
        formData.authMethod,
        context
      );
      const sensitiveFormFields = scenarioConfig?.sensitiveFormFieldNames ?? [];
      const processedAuthParams = { ...authParams };
      const fieldMapping = scenarioConfig?.fieldMapping ?? {};

      sensitiveFormFields.forEach((formFieldName) => {
        const backendKey = fieldMapping[formFieldName] ?? formFieldName;

        if (
          backendKey in processedAuthParams &&
          !processedAuthParams[backendKey]
        ) {
          processedAuthParams[backendKey] = null;
        }
      });

      // Add connection ID to the parameters
      const connectionParams = {
        ...processedAuthParams,
        id: connectionId
      };

      // Call test connection service
      const result = await agentScannerService.testConnection({
        targetSystemType: connection.type,
        connectionParams,
        context
      });

      if (result) {
        setConnectionStatus('success');

        return true;
      }
      setConnectionStatus('error');
      setConnectionError(
        'Connection test failed. Please verify your credentials.'
      );

      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Connection test error:', error);
      setConnectionStatus('error');
      setConnectionError(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to test connection. Please try again.'
      );

      if (error?.headerErrorMessage) {
        setConnectionHeaderError(error.headerErrorMessage);
      }

      return false;
    }
  }, [formData, context, initialData]);

  // Validate connection before submission (test connection only if needed)
  const validateConnection = useCallback(async () => {
    const shouldTestConnection = connectionStatus !== 'success';

    if (!shouldTestConnection) {
      // No need to test connection
      return true;
    }

    // Perform test connection
    const testResult = await handleTestConnection();

    if (!testResult) {
      // Update error message for connection test failure
      setConnectionError(
        'Connection test failed. Please verify your credentials before submitting.'
      );

      return false;
    }

    // Test connection succeeded
    return true;
  }, [connectionStatus, handleTestConnection]);

  // Form submission handler
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validate connection (similar to required fields validation)
      const connectionValid = await validateConnection();

      if (!connectionValid) {
        // Connection validation failed - don't submit
        return;
      }

      // Build form payload data
      const formPayloadData = buildFormPayload(
        formData,
        mode,
        modifiedSensitiveFields,
        context
      );

      // Transform to API payload format
      const apiPayload = transformFormToPayload(formPayloadData, {
        ownerId: context.ownerId || '',
        businessGroup: context.businessGroup || '',
        orgId: context.organizationId || '',
        connectionId:
          mode === 'edit'
            ? formData.connectionId || initialData?.connection?.id
            : undefined
      });

      // Add state to the payload (top level)
      apiPayload.state = scheduleStatus;

      // For edit mode, include the ID
      if (mode === 'edit' && (formData.id || initialData?.id)) {
        apiPayload.id = formData.id || initialData?.id;
      }

      if (onSubmit) {
        onSubmit(apiPayload);
      }
    },
    [
      formData,
      mode,
      initialData,
      onSubmit,
      context,
      modifiedSensitiveFields,
      validateConnection,
      scheduleStatus
    ]
  );

  // Form validation - synchronous
  const isFormValid = React.useMemo(() => {
    if (scheduleStatus === 'STOPPED') {
      return false;
    }

    return validateForm(formData, mode, context);
  }, [formData, mode, scheduleStatus, context]);

  return {
    // State
    formData,
    isInitialized: true,
    connectionStatus,
    connectionError,
    connectionHeaderError,
    showCustomModal,
    showAdvancedSettings,
    customSchedule,
    isFormValid,
    scheduleStatus,

    // Handlers
    handleChange,
    handleSubmit,
    handleTestConnection,
    handleCustomScheduleSave,
    handleCustomScheduleCancel,
    handleCustomScheduleChange,
    toggleAdvancedSettings,
    handleEmailNotificationsToggle,
    handleAddEmail,
    handleRemoveEmail,
    handleScheduleStatusToggle
  };
};

export default useScannerForm;
